import {
  Component,
  input,
  InputSignal,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { Shoutout } from '../../models/shoutout.model';
import { Event, EventPaymentEnum } from '../../models/event.model';
import { Product } from '../../models/product.model';
import {
  loadStripe,
  PaymentIntentResult,
  Stripe,
  StripeError,
} from '@stripe/stripe-js';
import { environment } from 'src/environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { shareShoutoutLink } from 'src/app/helpers/shareShoutoutLink';

@Component({
  selector: 'app-shoutout-create',
  templateUrl: './shoutout-create.component.html',
  styleUrls: ['./shoutout-create.component.scss'],
  standalone: false,
})
export class ShoutoutCreateComponent implements OnInit {
  eventId!: string | null;
  event$!: Observable<Event | undefined>;
  event!: Event;
  shoutoutForm!: UntypedFormGroup;
  shoutoutCollection: AngularFirestoreCollection<Shoutout>;
  confirmation: any;
  loading = false;
  stripeClientSecretFunc$!: any;
  stripeClientSecret!: string;
  stripe!: Stripe | null;
  stripeElements: any;
  paymentElement: any;
  stripePaymentFormComplete: boolean = false;
  stripePaymentMethod: string | null = null;
  stripeError!: StripeError;
  stripeProcessing: boolean = false;
  isKioskMode: InputSignal<boolean> = input(false, { alias: 'kioskMode' });
  EventPaymentEnum: typeof EventPaymentEnum = EventPaymentEnum;
  selectedProduct: WritableSignal<Product | null> = signal(null);
  selectedPrice: WritableSignal<number> = signal(0);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private afs: AngularFirestore,
    private fns: AngularFireFunctions,
    private fb: UntypedFormBuilder,
    private _snackBar: MatSnackBar,
  ) {
    this.shoutoutCollection = this.afs.collection<Shoutout>('shoutouts');
    this.route.paramMap.subscribe((params) => {
      this.eventId = params.get('eventId');
      this.loadEvent(this.eventId);
      this.shoutoutForm = this.fb.group({
        eventId: this.eventId,
        productId: [null, [Validators.required]],
        to: [
          '',
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(100),
          ],
        ],
        team: [
          '',
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(100),
          ],
        ],
        from: [
          '',
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(100),
          ],
        ],
        sessionId: null,
        message: [
          '',
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(255),
          ],
        ],
        paid: false,
        moderated: false,
        announced: false,
        approved: false,
      });
    });
  }

  loadEvent = async (eventId: string | null) => {
    this.event$ = this.afs
      .doc<Event>(`events/${eventId}`)
      .valueChanges({ idField: 'id' });
    this.event = (await firstValueFrom(this.event$)) as Event;
    if (this.event.sessions.length > 0) {
      this.shoutoutForm.controls['sessionId'].setValidators([
        Validators.required,
      ]);
      this.shoutoutForm.controls['sessionId'].updateValueAndValidity();
    }
    // Set default product if available
    if (this.event.products && this.event.products.length > 0) {
      const defaultProduct =
        this.event.products.find((p) => p.isDefault) || this.event.products[0];
      this.shoutoutForm.patchValue({ productId: defaultProduct.id });
      this.onProductChange(defaultProduct.id);
    }
  };

  onProductChange(productId: string) {
    const product = this.event.products?.find((p) => p.id === productId);
    if (product) {
      this.selectedProduct.set(product);
      this.selectedPrice.set(product.amount);
      // Reinitialize Stripe with new price
      if (!this.isKioskMode()) {
        this.initStripe();
      }
    }
  }

  initStripe = async () => {
    const productId = this.shoutoutForm.get('productId')?.value;
    if (!productId) return;

    this.stripe = await loadStripe(environment.stripe.publishableKey);
    this.stripeClientSecretFunc$ = this.fns.httpsCallable(
      'stripeShoutoutClientSecret',
    );
    this.stripeClientSecret = await firstValueFrom(
      this.stripeClientSecretFunc$({
        eventId: this.eventId,
        productId: productId,
      }),
    )
      .then((response: any) => {
        return response.clientSecret;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });

    // Unmount existing payment element if it exists
    if (this.paymentElement) {
      this.paymentElement.unmount();
    }

    this.stripeElements = this.stripe?.elements({
      clientSecret: this.stripeClientSecret,
    });

    this.paymentElement = this.stripeElements?.create('payment', {
      wallets: { applePay: 'auto', googlePay: 'auto' },
    });
    this.paymentElement?.mount('#stripe-payment-element');
    this.paymentElement?.on('change', this.stripeChangeHandler.bind(this));
  };

  ngOnInit(): void {
    if (!this.isKioskMode()) {
      this.initStripe();
    }
  }

  get to() {
    return this.shoutoutForm.get('to');
  }
  get team() {
    return this.shoutoutForm.get('team');
  }
  get from() {
    return this.shoutoutForm.get('from');
  }
  get message() {
    return this.shoutoutForm.get('message');
  }
  get sessionId() {
    return this.shoutoutForm.get('sessionId');
  }
  get productId() {
    return this.shoutoutForm.get('productId');
  }

  shareLink(): string {
    return shareShoutoutLink(this.event.id || null);
  }

  stripeChangeHandler(event: any) {
    this.stripePaymentFormComplete = event.complete;
    this.stripePaymentMethod = event.value?.type;
  }

  saveShoutout(
    shoutout: Shoutout,
    stripeResults: PaymentIntentResult | undefined = undefined,
  ) {
    if (stripeResults?.paymentIntent && !this.isKioskMode()) {
      shoutout.paymentIntentId = stripeResults.paymentIntent.id;
      shoutout.paymentMethod = stripeResults.paymentIntent.payment_method;
    } else if (this.isKioskMode()) {
      shoutout.paymentIntentId = `kiosk_${this.afs.firestore.collection('shoutouts').doc().id}`;
      shoutout.paymentMethod = 'kiosk';
    }
    shoutout.price = this.selectedPrice();
    shoutout.paid = true;
    this.shoutoutCollection.add(shoutout);
    if (this.isKioskMode()) {
      this.shoutoutForm.reset();
      // Reset to default product after submission
      if (this.event.products && this.event.products.length > 0) {
        const defaultProduct =
          this.event.products.find((p) => p.isDefault) ||
          this.event.products[0];
        this.shoutoutForm.patchValue({ productId: defaultProduct.id });
        this.onProductChange(defaultProduct.id);
      }
      this._snackBar.open('Shoutout successfully sent', 'Ok');
    } else {
      this.router.navigate(['events', this.eventId, 'shoutouts', 'confirmed'], {
        queryParams: { orderId: shoutout.paymentIntentId },
      });
    }
  }

  onSubmit = async () => {
    const shoutout = this.shoutoutForm.value as Shoutout;
    if (this.isKioskMode()) {
      this.saveShoutout(shoutout);
    } else {
      this.stripeProcessing = true;
      this.stripe
        ?.confirmPayment({
          elements: this.stripeElements,
          redirect: 'if_required',
        })
        .then((result) => {
          this.stripeProcessing = false;
          if (result.error) {
            this.stripeError = result.error;
          } else {
            this.saveShoutout(shoutout, result);
          }
        });
    }
  };
}
